import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Icon } from "../../components"

/* Moment */
import moment from "moment"
import "moment/locale/ru"

/* DatePicker */
import DatePicker, { registerLocale } from "react-datepicker"
import ru from "date-fns/locale/ru"

/* REST API */
import { organizations } from "../../api/Queue"

/* Shared */
import { STATUSES, SOURCES } from "./shared"

registerLocale("ru", ru)
moment.locale("ru")
Modal.setAppElement("#root")


/* The periods the tickets are looked at by. A queue lives one day, so the day is the one
   the page opens on */
export const PERIODS = [
    { key: "today", text: "Сегодня" },
    { key: "yesterday", text: "Вчера" },
    { key: "week", text: "Эта неделя" },
    { key: "month", text: "Этот месяц" },
    { key: "prev", text: "Прошлый месяц" },
    { key: "period", text: "Свой период" }
]


/* The dates one period stands for */
export const periodDates = key => {

    if (key === "yesterday") {
        return { startDate: moment().subtract(1, "day").startOf("day").toDate(), endDate: moment().subtract(1, "day").endOf("day").toDate() }
    }

    if (key === "week") {
        return { startDate: moment().startOf("week").toDate(), endDate: moment().endOf("week").toDate() }
    }

    if (key === "month") {
        return { startDate: moment().startOf("month").toDate(), endDate: moment().endOf("month").toDate() }
    }

    if (key === "prev") {
        return { startDate: moment().subtract(1, "month").startOf("month").toDate(), endDate: moment().subtract(1, "month").endOf("month").toDate() }
    }

    return { startDate: moment().startOf("day").toDate(), endDate: moment().endOf("day").toDate() }
}


/* The filter the page of the queue opens on — today, every point, every state, every source */
export const INITIAL_FILTER = { period: "today", organizationID: "all", status: "all", source: "all", ...periodDates("today") }


/* HOW MANY VALUES of the filter differ from the empty one — the button of the page shows it */
export const filterCount = filter => {

    let count = filter.period === INITIAL_FILTER.period ? 0 : 1

    if (`${filter.organizationID}` !== "all") {
        count = count + 1
    }

    if (`${filter.status}` !== "all") {
        count = count + 1
    }

    if (`${filter.source}` !== "all") {
        count = count + 1
    }

    return count
}


/* WHAT THE PERIOD IS CALLED — the name of a whole day or the dates of an own one */
export const periodTitle = filter => {

    const period = PERIODS.find(item => item.key === filter.period)

    if (period && period.key !== "period") {
        return period.text
    }

    return `${moment(filter.startDate).format("DD.MM.YY")} — ${moment(filter.endDate).format("DD.MM.YY")}`
}


/* Widget Filter Modal — the point, the period, the state and the source of the tickets.
   It is opened by the reference: this._filter.current.open(filter) */
class FilterModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            filter: { ...INITIAL_FILTER },

            /* The points are read once and kept for the whole life of the page */
            list: [],
            search: ""
        }
    }

    /* Open the filter on what the page is showing now */
    open = filter => {
        this.setState({ visible: true, search: "", filter: { ...INITIAL_FILTER, ...filter } }, () => this.load())
    }

    /* Close the filter */
    close = () => {
        this.setState({ visible: false })
    }

    /* The points which keep a queue — they are asked for only once */
    load = () => {

        /* Fields */
        const { list } = this.state

        /* Check Data */
        if (list.length > 0) {
            return
        }

        organizations().then(response => {
            if (response && response.status === 200) {
                this.setState({ list: response.data.list })
            }
        }).catch(() => this.setState({ list: [] }))
    }

    /* Choose one value of the filter */
    set = (key, value) => {
        this.setState(previous => ({ filter: { ...previous.filter, [key]: value } }))
    }

    /* Choose the period — its dates are filled in at once */
    period = key => {

        /* The own period keeps the dates which are already chosen */
        if (key === "period") {
            this.set("period", key)
            return
        }

        this.setState(previous => ({ filter: { ...previous.filter, period: key, ...periodDates(key) } }))
    }

    /* Give the chosen filter to the page */
    apply = () => {

        const { filter } = this.state
        const { onApply } = this.props

        this.setState({ visible: false })
        onApply(filter)
    }

    /* Drop everything back to today */
    reset = () => {

        const { onApply } = this.props

        this.setState({ visible: false, search: "", filter: { ...INITIAL_FILTER } })
        onApply({ ...INITIAL_FILTER })
    }

    /* The point of the ticket — only the ones which really keep a queue are here, so the
       list is short, but it is searched through by the name all the same */
    _organizations = () => {

        /* Fields */
        const { list, search, filter } = this.state

        const found = search.length > 0
            ? list.filter(item => `${item.name}`.toLowerCase().indexOf(search.toLowerCase()) > -1)
            : list

        const chosen = list.find(item => `${item.id}` === `${filter.organizationID}`)

        return (
            <div className="filter-modal-row">

                <div className="filter-modal-row-text">Организация</div>

                <div className="queue-filter-search">

                    <Icon name="search" size={16} />

                    <input
                        placeholder={chosen ? chosen.name : "Все организации"}
                        value={search}
                        onChange={event => this.setState({ search: event.target.value })}
                    />

                    {search.length > 0 &&
                        <div onClick={() => this.setState({ search: "" })} className="queue-filter-clear">
                            <Icon name="close" size={10} width={2.4} />
                        </div>
                    }

                </div>

                <div className="queue-filter-list">

                    <div
                        onClick={() => this.set("organizationID", "all")}
                        className={`queue-filter-option ${`${filter.organizationID}` === "all" ? "active" : ""}`}
                    >
                        Все организации
                    </div>

                    {found.map(item =>
                        <div
                            key={`${item.id}`}
                            onClick={() => this.set("organizationID", item.id)}
                            className={`queue-filter-option ${`${filter.organizationID}` === `${item.id}` ? "active" : ""}`}
                        >
                            {item.name}
                            <span>{item.count}</span>
                        </div>
                    )}

                    {found.length === 0 && <div className="queue-filter-empty">Ничего не найдено</div>}

                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, filter } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="filter-modal"
                overlayClassName="filter-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="filter-modal-header">
                    <h2>Фильтры очереди</h2>
                    <div onClick={this.close} className="filter-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                <div className="filter-modal-body">

                    {this._organizations()}

                    <div className="filter-modal-row">

                        <div className="filter-modal-row-text">Период</div>

                        <div className="filter-modal-chips">
                            {PERIODS.map(period =>
                                <div
                                    key={period.key}
                                    onClick={() => this.period(period.key)}
                                    className={`filter-modal-chip ${filter.period === period.key ? "active" : ""}`}
                                >
                                    {period.text}
                                </div>
                            )}
                        </div>

                        <div className="filter-modal-dates">

                            <div className="filter-modal-date">
                                <span>С</span>
                                <DatePicker
                                    withPortal
                                    locale="ru"
                                    dateFormat="dd.MM.yyyy"
                                    selected={filter.startDate}
                                    onChange={date => this.setState(previous => ({ filter: { ...previous.filter, period: "period", startDate: date } }))}
                                />
                            </div>

                            <div className="filter-modal-date">
                                <span>По</span>
                                <DatePicker
                                    withPortal
                                    locale="ru"
                                    dateFormat="dd.MM.yyyy"
                                    selected={filter.endDate}
                                    onChange={date => this.setState(previous => ({ filter: { ...previous.filter, period: "period", endDate: date } }))}
                                />
                            </div>

                        </div>

                    </div>

                    <div className="filter-modal-row">

                        <div className="filter-modal-row-text">Статус талона</div>

                        <div className="filter-modal-chips">

                            <div onClick={() => this.set("status", "all")} className={`filter-modal-chip ${`${filter.status}` === "all" ? "active" : ""}`}>Все</div>

                            {STATUSES.map(item =>
                                <div
                                    key={item.status}
                                    onClick={() => this.set("status", item.status)}
                                    className={`filter-modal-chip ${filter.status === item.status ? "active" : ""}`}
                                >
                                    {item.text}
                                </div>
                            )}

                        </div>

                    </div>

                    <div className="filter-modal-row">

                        <div className="filter-modal-row-text">Откуда встал в очередь</div>

                        <div className="filter-modal-chips">

                            <div onClick={() => this.set("source", "all")} className={`filter-modal-chip ${filter.source === "all" ? "active" : ""}`}>Все</div>

                            {Object.keys(SOURCES).map(key =>
                                <div
                                    key={key}
                                    onClick={() => this.set("source", key)}
                                    className={`filter-modal-chip ${filter.source === key ? "active" : ""}`}
                                >
                                    {SOURCES[key]}
                                </div>
                            )}

                        </div>

                    </div>

                </div>

                <div className="filter-modal-footer">
                    <div onClick={() => this.reset()} className="filter-modal-button light">Сбросить</div>
                    <div onClick={() => this.apply()} className="filter-modal-button">Показать</div>
                </div>

            </Modal>
        )
    }
}

export default FilterModal
